import { useCallback, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, newClientMsgId, type LiveVisitor, type Site } from "../../api";
import { useDialogs } from "../../components/Dialog";
import { agoPhrase, visitorLabel } from "../../lib/format";
import { useI18n } from "../../lib/i18n";
import { deviceLabel, placeLabel } from "../../lib/place";
import { apiErrorText } from "../../lib/apiError";

/**
 * Who is on the sites right now.
 *
 * The inbox only ever showed the people who had already written. This is
 * everyone with a page open that carries the widget — so an operator can see
 * that someone has been sitting on the checkout page for four minutes, and
 * write to them first.
 *
 * Polled rather than pushed: the list changes with every page load on every
 * site, and a frame per page view to every open console would be most of the
 * traffic on the socket for a screen that is usually not the one on show.
 */

const REFRESH_MS = 10_000;

/** Only http(s) becomes a link; anything else is shown as text. */
function safeHref(url: string): string | null {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:" ? u.toString() : null;
  } catch {
    return null;
  }
}

function pathOf(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname + u.search || "/";
  } catch {
    return url;
  }
}

export function VisitorsView({ onOpen }: { onOpen: (conversationId: number) => void }): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.visitors;
  const dialogs = useDialogs();
  const [visitors, setVisitors] = useState<LiveVisitor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  /** Every site this operator serves, so one with nobody on it still shows a 0. */
  const [sites, setSites] = useState<Site[]>([]);
  /** Showing one site's visitors only; null for all of them. */
  const [siteFilter, setSiteFilter] = useState<number | null>(null);

  useEffect(() => {
    api
      .sites()
      .then((res) => setSites(res.sites))
      .catch(() => undefined);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.liveVisitors();
      setVisitors(res.visitors);
      setNow(Date.now());
      setError(null);
    } catch {
      setError(t.loadFailed);
    }
  }, [t.loadFailed]);

  useEffect(() => {
    void load();
    // Not while the tab is hidden: nobody is looking, and the list is one
    // request away when they come back.
    const timer = window.setInterval(() => {
      if (!document.hidden) void load();
    }, REFRESH_MS);
    const onVisible = (): void => {
      if (!document.hidden) void load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  async function start(visitor: LiveVisitor, name: string): Promise<void> {
    const body = await dialogs.prompt({
      title: t.startTitle(name),
      body: t.startBody,
      label: t.startLabel,
      placeholder: t.startPlaceholder,
      multiline: true,
      maxLength: 4000,
      confirmLabel: all.composer.send,
    });
    if (!body) return;
    setBusy(visitor.visitorId);
    try {
      const res = await api.startConversation(visitor.visitorId, body, newClientMsgId());
      onOpen(res.conversationId);
    } catch (err) {
      setError(apiErrorText(err, all, t.startFailed));
    } finally {
      setBusy(null);
    }
  }

  // Sites in the operator's own order, then any a visitor is on that the list
  // above did not have (a site made since this page opened).
  const perSite = new Map<number, { name: string; count: number }>();
  for (const site of sites) perSite.set(site.id, { name: site.name, count: 0 });
  for (const visitor of visitors ?? []) {
    const entry = perSite.get(visitor.siteId) ?? { name: visitor.siteName, count: 0 };
    entry.count += 1;
    perSite.set(visitor.siteId, entry);
  }
  const shown = siteFilter === null ? visitors : (visitors ?? []).filter((v) => v.siteId === siteFilter);

  return (
    <div class="stats-page visitors-page">
      <header class="stats-head">
        <h1>
          {t.title}
          {visitors && visitors.length > 0 ? <span class="hint head-count">{t.count(visitors.length)}</span> : null}
        </h1>
      </header>
      <p class="hint" style={{ margin: "-8px 0 14px" }}>
        {t.intro}
      </p>

      {perSite.size > 1 ? (
        <div class="site-counts" role="group" aria-label={t.perSite}>
          <button type="button" class="site-count" aria-pressed={siteFilter === null} onClick={() => setSiteFilter(null)}>
            <span class="site-count-name">{t.allSites}</span>
            <span class="site-count-n">{visitors?.length ?? 0}</span>
          </button>
          {[...perSite.entries()].map(([id, entry]) => (
            <button
              key={id}
              type="button"
              class={`site-count${entry.count > 0 ? " live" : ""}`}
              aria-pressed={siteFilter === id}
              onClick={() => setSiteFilter(siteFilter === id ? null : id)}
            >
              <span class="site-count-name">{entry.name}</span>
              <span class="site-count-n">{entry.count}</span>
            </button>
          ))}
        </div>
      ) : null}

      {error ? (
        <div class="alert error" role="alert">
          {error}
        </div>
      ) : null}

      {shown === null ? (
        error ? null : <p class="hint">{all.common.loading}</p>
      ) : shown.length === 0 ? (
        <div class="card empty-card">
          <strong>{t.empty}</strong>
          <span class="hint">{t.emptyHint}</span>
        </div>
      ) : (
        <div class="table-wrap">
          <table class="data-table visitors">
            <thead>
              <tr>
                <th scope="col">{t.who}</th>
                <th scope="col">{all.common.site}</th>
                <th scope="col">{t.location}</th>
                <th scope="col">{t.deviceCol}</th>
                <th scope="col">{t.page}</th>
                <th scope="col">{t.since}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {shown.map((visitor) => {
                const name = visitorLabel(
                  { visitorName: visitor.displayName, visitorEmail: visitor.email, visitorId: visitor.visitorId },
                  lang,
                );
                const href = visitor.page ? safeHref(visitor.page.url) : null;
                return (
                  <tr key={visitor.visitorId}>
                    <td>
                      <strong>{name}</strong>
                      {visitor.identityVerified ? (
                        <span class="chip verified" title={all.context.verifiedTitle} style={{ marginLeft: "6px" }}>
                          ✓
                        </span>
                      ) : null}
                      <span class="hint block">
                        {[visitor.displayName ? visitor.email : null, visitor.tabs > 1 ? t.tabs(visitor.tabs) : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </td>
                    <td>{visitor.siteName}</td>
                    <td class="nowrap">
                      {placeLabel(visitor.country, lang) ?? <span class="hint">—</span>}
                      {visitor.ip ? <code class="ip block">{visitor.ip}</code> : null}
                    </td>
                    <td class="nowrap">
                      {deviceLabel(visitor.device, t.devices) ?? <span class="hint">—</span>}
                      {visitor.browser || visitor.os ? (
                        <span class="hint block">{[visitor.browser, visitor.os].filter(Boolean).join(" · ")}</span>
                      ) : null}
                    </td>
                    <td class="page-cell">
                      {visitor.page ? (
                        href ? (
                          <a href={href} target="_blank" rel="noopener noreferrer" title={visitor.page.url}>
                            {visitor.page.title || pathOf(visitor.page.url)}
                          </a>
                        ) : (
                          visitor.page.title || visitor.page.url
                        )
                      ) : (
                        <span class="hint">{t.unknownPage}</span>
                      )}
                      {visitor.page?.title ? <span class="hint block">{pathOf(visitor.page.url)}</span> : null}
                    </td>
                    <td class="nowrap">{agoPhrase(visitor.since, now, lang)}</td>
                    <td class="nowrap">
                      {visitor.conversationId ? (
                        <button class="btn sm" onClick={() => onOpen(visitor.conversationId!)}>
                          {t.openChat}
                        </button>
                      ) : (
                        <button class="btn sm primary" disabled={busy === visitor.visitorId} onClick={() => void start(visitor, name)}>
                          {t.start}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {dialogs.dialog}
    </div>
  );
}
